import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, ArrowRight } from "lucide-react";
import api from "../lib/api";
import { SERVICES, BUSINESS } from "../lib/site";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";

const field = "w-full bg-white border-input text-ink h-11";
const label = "text-xs font-heading font-bold uppercase tracking-wider text-ink/70";

export default function BookingForm({ compact = false }) {
  const [params] = useSearchParams();
  const [form, setForm] = useState({
    name: "", phone: "", email: "", vehicle_reg: "", vehicle_make: "", vehicle_model: "",
    service: params.get("service") || "", preferred_date: "", preferred_time: "", info: "",
  });
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target ? e.target.value : e });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/bookings", form);
      setDone(true);
    } catch (err) {
      toast.error("Could not submit your booking request. Please call us instead.");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div data-testid="booking-success" className="bg-pale border border-brand/20 rounded-lg p-8 text-center">
        <CheckCircle2 className="mx-auto text-brand mb-4" size={44} />
        <h3 className="font-heading font-extrabold text-2xl text-ink mb-2">Booking request received</h3>
        <p className="text-muted-foreground text-sm leading-relaxed max-w-md mx-auto">
          Thank you, {form.name.split(" ")[0]}. We have received your request for a <strong className="text-ink">{form.service}</strong>.
          We will contact you shortly to confirm your appointment. Need us sooner? Call{" "}
          <a href={BUSINESS.phoneHref} className="text-brand font-semibold">{BUSINESS.phone}</a>.
        </p>
      </div>
    );
  }

  return (
    <form data-testid="booking-form" onSubmit={submit} className={`grid grid-cols-1 sm:grid-cols-2 gap-4 ${compact ? "" : "lg:gap-5"}`}>
      <div>
        <Label htmlFor="bk-name" className={label}>Name *</Label>
        <Input id="bk-name" data-testid="booking-name" required value={form.name} onChange={set("name")} className={field} placeholder="Your full name" />
      </div>
      <div>
        <Label htmlFor="bk-phone" className={label}>Phone *</Label>
        <Input id="bk-phone" data-testid="booking-phone" required type="tel" value={form.phone} onChange={set("phone")} className={field} placeholder="07xxx xxx xxx" />
      </div>
      <div>
        <Label htmlFor="bk-email" className={label}>Email *</Label>
        <Input id="bk-email" data-testid="booking-email" required type="email" value={form.email} onChange={set("email")} className={field} placeholder="you@example.com" />
      </div>
      <div>
        <Label htmlFor="bk-reg" className={label}>Vehicle Registration</Label>
        <Input id="bk-reg" data-testid="booking-reg" value={form.vehicle_reg} onChange={set("vehicle_reg")} className={`${field} font-mono uppercase`} placeholder="AB12 CDE" />
      </div>
      <div>
        <Label htmlFor="bk-make" className={label}>Vehicle Make</Label>
        <Input id="bk-make" data-testid="booking-make" value={form.vehicle_make} onChange={set("vehicle_make")} className={field} placeholder="e.g. Ford" />
      </div>
      <div>
        <Label htmlFor="bk-model" className={label}>Vehicle Model</Label>
        <Input id="bk-model" data-testid="booking-model" value={form.vehicle_model} onChange={set("vehicle_model")} className={field} placeholder="e.g. Focus" />
      </div>
      <div className="sm:col-span-2">
        <Label className={label}>Service Required *</Label>
        <Select required value={form.service} onValueChange={set("service")}>
          <SelectTrigger data-testid="booking-service" className={field}>
            <SelectValue placeholder="Select a service" />
          </SelectTrigger>
          <SelectContent>
            {SERVICES.map((s) => (
              <SelectItem key={s} value={s} data-testid={`service-option-${s.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>{s}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label htmlFor="bk-date" className={label}>Preferred Date</Label>
        <Input id="bk-date" data-testid="booking-date" type="date" value={form.preferred_date} onChange={set("preferred_date")} className={field} />
      </div>
      <div>
        <Label className={label}>Preferred Time</Label>
        <Select value={form.preferred_time} onValueChange={set("preferred_time")}>
          <SelectTrigger data-testid="booking-time" className={field}>
            <SelectValue placeholder="Any time" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="Morning" data-testid="time-morning">Morning</SelectItem>
            <SelectItem value="Afternoon" data-testid="time-afternoon">Afternoon</SelectItem>
            <SelectItem value="Any" data-testid="time-any">Any time</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="sm:col-span-2">
        <Label htmlFor="bk-info" className={label}>Additional Information</Label>
        <Textarea id="bk-info" data-testid="booking-info" rows={compact ? 3 : 4} value={form.info} onChange={set("info")} className="w-full bg-white border-input text-ink" placeholder="Anything we should know about the vehicle or the work required..." />
      </div>
      <div className="sm:col-span-2">
        <button
          type="submit"
          data-testid="booking-submit-btn"
          disabled={busy}
          className="btn-primary w-full sm:w-auto disabled:opacity-60"
        >
          {busy ? "SENDING..." : "REQUEST BOOKING"} <ArrowRight size={17} />
        </button>
        <p className="text-xs text-muted-foreground mt-3">
          Prefer to talk? Call <a href={BUSINESS.phoneHref} className="text-brand font-semibold">{BUSINESS.phone}</a> or email{" "}
          <a href={`mailto:${BUSINESS.email}`} className="text-brand font-semibold">{BUSINESS.email}</a>.
        </p>
      </div>
    </form>
  );
}
